import React, {createRef, useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {styled} from '@mui/material/styles';
import {API} from 'aws-amplify';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import AuthService from '../auth/services/AuthService';
import MenuDrawer from '../home/components/MenuDrawer';
import baseStyles from '../css/Base.module.css';
import themeStyles from '../home/assets/stylesheets/ufli-theme.module.css';
import ImageLogo from '../home/assets/images/logos/flamingo/lockup/dark.svg';
import {ENDPOINT} from '../constants';

const PREFIX = 'StudentJoinCode';
const classes = {
  iconButton: `${PREFIX}-iconButton`,
};
const Root = styled('div')({
  [`& .${classes.iconButton}`]: {
    backgroundColor: '#EE4028',
    '&:hover': {
      backgroundColor: '#f26a57',
    },
  },
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function StudentLogin() {
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [isMenuOpen, toggleMenu] = useState(false);
  const [isError, setIsError] = useState(false);
  const [code, setCode] = useState(new Array(6).fill('')); // Initializes the array with empty strings
  const refs = new Array(6).fill(0).map(() => createRef()); // Creates 6 refs

  useEffect(() => {
    const authCode = code.join('');
    if (authCode.length === 6) {
      const fetchStudent = async () => {
        try {
          await sleep(300); // Short delay to allow the user to see the last input
          setIsLoading(true);
          await API.get(ENDPOINT, `/students/auth-code/${authCode}`);
          history.push(`/student/dashboard?authCode=${authCode}`);
        } catch (error) {
          setIsError(true);
        } finally {
          setIsLoading(false);
        }
      };
      fetchStudent();
    }
  }, [code, history]);

  async function handleClickAvatar() {
    const {isAuthenticated} = await AuthService.checkAuthentication();
    if (isAuthenticated) history.push('/builder');
    else toggleMenu(true);
  }

  // Upper and lower case letters and numbers
  const isValidChar = (char) => /^[a-zA-Z0-9]$/.test(char);

  const handleInput = (e, index) => {
    const value = e.target.value;
    const next = refs[index + 1];
    const prev = refs[index - 1];

    if (e.key === 'Backspace') {
      if (value) {
        setCode((code) => {
          const newCode = [...code];
          newCode[index] = '';
          return newCode;
        });
      } else if (!value && prev) {
        setCode((code) => {
          const newCode = [...code];
          newCode[index - 1] = '';
          return newCode;
        });
        prev.current.focus();
      }
    } else if (isValidChar(e.key) && value.length < 1 && (next || index === code.length - 1)) {
      setCode((code) => {
        const newCode = [...code];
        newCode[index] = e.key.toUpperCase();
        return newCode;
      });
      if (next) next.current.focus();
    }
  };

  const handleTryAgain = () => {
    setIsError(false);
    setCode(new Array(6).fill(''));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    const pasteArray = paste.split('').slice(0, 6); // Only use the first 6 characters
  
    if (pasteArray.length === 6 && pasteArray.every((char) => isValidChar(char))) {
      setCode(pasteArray.map((char) => char.toUpperCase()));
    }
  };

  const renderInput = (value, index) => (
    <TextField
      key={index}
      autoFocus={index === 0}
      type="text"
      maxLength="1"
      value={value}
      onKeyDown={(e) => handleInput(e, index)}
      onPaste={handlePaste}
      inputRef={refs[index]}
      autoComplete="off"
      inputProps={{
        style: {textAlign: 'center', fontSize: 96, minHeight: 144},
        maxLength: 1,
      }}
    />
  );

  const renderError = () => (
    <Grid xs display="flex" justifyContent="center" alignItems="center" flexDirection="column">
      <Typography variant="h5" className="m-t-20 m-b-10">
        Code not recognized.
      </Typography>
      <Button variant="contained" onClick={handleTryAgain}>
        TRY AGAIN
      </Button>
    </Grid>
  );

  return (
    <Root>
      <AppBar position="sticky" className={'bg-white'}>
        <Toolbar>
          <Typography variant="h6" className={baseStyles['f-g-1']}></Typography>

          <IconButton color="inherit" className={classes.iconButton} onClick={handleClickAvatar} size="large">
            <PersonOutlineOutlinedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <MenuDrawer isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} />
      <div className="p-20">
        <img src={ImageLogo} alt="" style={{width: '350px'}} />
        <Typography variant="h1" className="m-t-20 fnt-bold clr-brandblue">
          Ready. Set. Go!
        </Typography>
        <Typography variant="h4" className="m-t-20 clr-brandblue">
          Have a join code? Enter it below.
        </Typography>
        <Grid container spacing={1} className="m-t-20">
          {isLoading && (
            <Grid xs display="flex" justifyContent="center" alignItems="center">
              <div className={baseStyles['progress-container']}>
                <CircularProgress />
              </div>
            </Grid>
          )}

          {isError && !isLoading && renderError()}

          {!isError &&
            !isLoading &&
            code.map((value, index) => (
              <Grid xs={2} key={index}>
                <Box>{renderInput(value, index)}</Box>
              </Grid>
            ))}
        </Grid>
      </div>
      <div style={{position: 'fixed', bottom: '0px', width: '100%'}}>
        <section className={[themeStyles['sec-footer'], 'bg-light-blue', 'clr-dark-blue'].join(' ')}>
          <div className={themeStyles['fl-grid']}>
            <div className={themeStyles['fl-col']}>
              <h6>Copyright 2020 University of Florida Lastinger Center</h6>
            </div>
            <div className={themeStyles['fl-col']}>
              <div className={[themeStyles['fl-col-inner-row'], themeStyles['footer-icons']].join(' ')}>
                <a href="https://www.facebook.com/LastingerCenter" target="_blank" rel="noopener noreferrer">
                  <div className={[themeStyles['footer-icon'], themeStyles['footer-icon-facebook']].join(' ')}></div>
                </a>
                <a href="https://twitter.com/lastingercenter" target="_blank" rel="noopener noreferrer">
                  <div className={[themeStyles['footer-icon'], themeStyles['footer-icon-twitter']].join(' ')}></div>
                </a>
                <a href="https://www.instagram.com/lastingercenter" target="_blank" rel="noopener noreferrer">
                  <div className={[themeStyles['footer-icon'], themeStyles['footer-icon-instagram']].join(' ')}></div>
                </a>
                <a href="https://www.linkedin.com/company/lastingercenter" target="_blank" rel="noopener noreferrer">
                  <div className={[themeStyles['footer-icon'], themeStyles['footer-icon-linkedin']].join(' ')}></div>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Root>
  );
}
